import BaseService from '../../base/base_service';
import ICountryService, { CountryServiceInitParams } from './country_service_interface';
import {IGeoLocationInfo} from "../../../../common/interfaces/geo-location-info.interface";
import {SYSTEM_EVENTS} from "../../events/events";
import Device from "../../utils/device/device";
import {Check} from "@sparkware/app-utils";
import IStateOfResidence from "../../../../common/interfaces/state-of-residence.interface";

class CountryService extends BaseService implements ICountryService{

  static CLASS: string = 'CountryService';

  private _initParams: CountryServiceInitParams = null;

  protected init(params: CountryServiceInitParams): void {
    super.init(params);
    this._setInitParams(params);
    this._subscribe();
  }

  private _subscribe() {
    this.onSystemEvent(SYSTEM_EVENTS.ON_INIT_COMPLETE, ()=>{
      if(this._shouldUpdateCountryByIP()) {
        this._countryByIP().then(()=> {
          this.checkIfCorrectAppInUse();
        })
      }
    });
  }

  private _setInitParams(params: CountryServiceInitParams): void {
    this._initParams = {
      default: params.default.toUpperCase(),
      country: params.country?.toUpperCase(),
      state: params.state ? params.state.toUpperCase() : '',
      uri: params.uri,
      supportLoginAndRegisterRedirect: params.supportLoginAndRegisterRedirect,

      anonymousCountryTTL: params.anonymousCountryTTL,
      mustCallCountryByIPApiFlag: params.mustCallCountryByIPApiFlag,
      countryName: params.countryName,
      supportedStates: Check.isNullOrUndefined(params.supportedStates) ? [] : params.supportedStates
    };
  }

  /**
   * Returns the default country value
   *
   * Added in case the server sends jibberish instead of a valid country
   *
   * @returns {*}
   */
  public getDefaultCountry(): string {
    return this._initParams.default;
  }


  /**
   * Returns the computed country or the default if country does not exist
   *
   * @returns {*}
   */
  public getCountry(): string {
    return this._initParams.country || this._initParams.default;
  }

  get countryName() {
    return this._initParams.countryName;
  }

  get supportedStates(): IStateOfResidence[] {
    return this._initParams.supportedStates;
  }

  /**
   * override base service for async jobs
   * @returns {Promise}
   * @public
   */
  public doAsyncJobs(): Promise<any> {
    if (this._initParams.country && this._initParams.mustCallCountryByIPApiFlag !== true) {
      return Promise.resolve();
    } else {
      return this._countryByIP();
    }
  }

  public isNetherlands(): boolean {
    if(this.serviceFactory.oAuth2Service().isAuthorized()) {
      const accountData = this.serviceFactory.userService().getAccountData();

      return accountData
        && accountData.RegistrationCountry
        && accountData.RegistrationCountry.toLowerCase() === 'nld';
    } else {
      return this.getCountry().toLowerCase() === 'nld';
    }
  }

  public isUK(): boolean {
    const currentCountry = (this.getCountry() || '').toUpperCase();
    return currentCountry === 'GB' || currentCountry === 'GBR';
  }

  checkIfCorrectAppInUse() {
    if(!this.serviceFactory.skinService().disableCheckIfCorrectAppInUse()) {
      this.serviceFactory.regulationService().checkIfCorrectAppInUse(this._initParams.country?.toUpperCase(), this._initParams.state?.toUpperCase());
    }
  }

  private _geoLocationInfo: IGeoLocationInfo = null;

  async getGeoLocationInfo():  Promise<IGeoLocationInfo | null> {
    if(this._geoLocationInfo) {
      return this._geoLocationInfo;
    }

    return await this._countryByIP();
  }
  /**
   * Get user country by IP
   *
   * @param url
   * @returns {Promise.<T>|*|Promise}
   * @private
   */
  private _countryByIP(): Promise<IGeoLocationInfo | null> {
    return this.serviceFactory.http2Service().post(this._initParams.uri).then((geolocationResponse: any) => {
      if(!this._initParams.country) {
        //This is done here mostly for the test environments.
        //In general, we get the country from infra in the window.webData. In the test environments this is set in the orbit launcher.
        //However, this _countryByIP call in the test environments will override the country set from launcher with obviously the wrong country because we are not in the country set from launcher.
        //this._initParams.country is initialized from sdk-initializer.ts which in test is from the launcher and in production is from webData.
        this._initParams.country = geolocationResponse.countryISO3?.toUpperCase();
      }

      this._initParams.state = geolocationResponse.state;
      this._initParams.stateId = geolocationResponse.stateId;
      this._geoLocationInfo = geolocationResponse;
      return this._geoLocationInfo;
    })
      .catch((err) => {
        this.logger.error('Could not get country by IP. Error : ' + err);
        return null
      });
  }

    private _shouldUpdateCountryByIP() {
        return Device.hybrid.inCasino && Device.os.isIOS && this.serviceFactory.regulationService().shouldCallCountryByIP(this._initParams.country?.toUpperCase());
    }
}

export default CountryService;